<template>
	<div class="driver-sidebar-row cursor-pointer border-bottom" :class="[rowClass, { 'has-stats': isExpanded }]" @click="$emit('select')">
		<div v-if="selectable" class="checkbox-col d-flex align-center justify-center" @click.stop="$emit('toggle-select', person)">
			<v-checkbox-btn :model-value="selected" density="compact" hide-details color="primary" />
		</div>

		<div class="name-col d-flex align-center h-100 pl-3 pr-1" :class="{ 'highlighted-duty': isHighlighted }">
			<span class="person-name text-truncate">{{ person.lastName }}, {{ person.firstName }}</span>
		</div>

		<div class="icons-col d-flex align-center justify-center border-right pr-1">
			<router-link
				:to="'/user/' + person.id.split('_')[0]"
				class="ma-0 pa-0 border-0 bg-transparent cursor-pointer d-inline-flex align-center justify-center text-decoration-none"
				style="width: 20px; height: 20px"
				@click.stop="$emit('select')"
			>
				<v-icon size="14" color="grey-darken-1">mdi-account-circle</v-icon>
			</router-link>

			<v-menu :close-on-content-click="false" location="bottom start">
				<template v-slot:activator="{ props: menuProps }">
					<v-icon
						v-bind="menuProps"
						size="14"
						color="grey"
						class="ml-1 cursor-pointer"
						icon="mdi-poll"
						@click.stop="$emit('open-stats-menu', person)"
					/>
				</template>

				<v-card class="driver-stats-menu pa-0">
					<v-list-item class="py-3">
						<template v-slot:prepend>
							<v-icon icon="mdi-poll" class="poll-icon-size"></v-icon>
						</template>
						<v-list-item-title class="text-body-1 font-weight-bold"> {{ $t('scheduler.statistics') }} </v-list-item-title>
					</v-list-item>

					<v-divider></v-divider>

					<v-list density="compact" class="py-0 text-caption">
						<template v-for="option in statOptions" :key="option.value">
							<v-list-item v-if="option.value === 'preferences_fulfilment'">
								<template v-slot:prepend>
									<v-icon :icon="option.icon" class="poll-icon-size"></v-icon>
								</template>
								<v-list-item-title class="text-caption pr-4">{{ option.text }}</v-list-item-title>
								<template v-slot:append>
									<v-icon size="14" :color="satisfactionStatus.color">
										{{ satisfactionStatus.icon }}
									</v-icon>
								</template>
							</v-list-item>

							<v-list-item v-else>
								<template v-slot:prepend>
									<v-icon :icon="option.icon" class="poll-icon-size"></v-icon>
								</template>
								<v-list-item-title class="text-caption pr-4">{{ option.text }}</v-list-item-title>
								<template v-slot:append>
									<span class="text-caption text-grey-darken-1 font-weight-medium">{{ getStatValue(option.value) }}</span>
								</template>
							</v-list-item>
						</template>
					</v-list>
				</v-card>
			</v-menu>
		</div>

		<div v-if="isExpanded" class="stats-col h-100" :style="dynamicStatsStyle">
			<DriverStats v-if="!props.statLoading" :key="`${person.id}-${statsRenderKey}`" :selectedColumns="selectedStats" :driver="person" />
			<v-skeleton-loader v-else class="w-100" height="24" type="text" />
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useDriverStatistics } from '@/rennovation/planning-view/services/useDriverStats';
import { useDriverStatsStyles } from '@/rennovation/planning-view/composables/useDriverStatsStyles';
import DriverStats from '../../Presentation/driverStats.vue';
import { StatOption } from '@/rennovation/planning-view/types/components/types';
import { usePlanningBridgeStore } from '@/rennovation/planning-view/store/bridge/planningBridge';

const props = withDefaults(
	defineProps<{
		person: any;
		index: number;
		rowClass: any;
		isHighlighted: boolean;
		isExpanded: boolean;
		statLoading: boolean;
		selectedStats: any[];
		statOptions: StatOption[];
		selectable?: boolean;
		selected?: boolean;
	}>(),
	{
		selectable: false,
		selected: false,
	}
);

defineEmits(['select', 'open-stats-menu', 'toggle-select']);

const { satisfactionStatus, getStatValue } = useDriverStatistics(() => props.person);
const { dynamicStatsStyle } = useDriverStatsStyles(() => props.selectedStats, {
	columnWidth: 80,
	extraColumns: () => (usePlanningBridgeStore().editor.superuser ? 1 : 0),
});

const statsRenderKey = ref(0);

const forceUpdateStats = () => {
	statsRenderKey.value++;
};

const handleSocketStatsRefresh = (event: Event) => {
	const customEvent = event as CustomEvent<{ driverId: string }>;
	if (customEvent.detail?.driverId === props.person?.id) {
		forceUpdateStats();
	}
};

onMounted(() => {
	window.addEventListener('socket:refresh-driver-stats', handleSocketStatsRefresh);
});

onUnmounted(() => {
	window.removeEventListener('socket:refresh-driver-stats', handleSocketStatsRefresh);
});
</script>

<style lang="scss" scoped>
.driver-sidebar-row {
	position: sticky;
	left: 0;
	z-index: 15;
	background-color: #ffffff;
	height: 100%;
	display: flex;
	align-items: center;
	width: fit-content;
}

.checkbox-col {
	width: 36px !important;
	min-width: 36px !important;
	max-width: 36px !important;
	flex: 0 0 36px !important;
	box-sizing: border-box;
}

/* COLUMN 1: STRICT 230px */
.name-col {
	width: 230px !important;
	min-width: 230px !important;
	max-width: 230px !important;
	flex: 0 0 230px !important;
	overflow: hidden;
	box-sizing: border-box;
}

.person-name {
	font-weight: 500;
	font-size: 13px;
	color: #1e293b;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	width: 100%;
}

/* COLUMN 2: STRICT 60px */
.icons-col {
	width: 60px !important;
	min-width: 60px !important;
	max-width: 60px !important;
	flex: 0 0 60px !important;
	box-sizing: border-box;
}

/* COLUMN 3: STATS AREA (Inherits exact fixed inline width from dynamicStatsStyle) */
.stats-col {
	display: flex;
	align-items: center;
	font-size: 12px;
	overflow: hidden;
	flex-shrink: 0;
	box-sizing: border-box;
}

.driver-stats-menu {
	width: auto;
	min-width: 220px;
	max-width: 340px;
	white-space: nowrap;
}

.poll-icon-size {
	font-size: 16px;
	margin-right: 8px;
}

.border-bottom {
	border-bottom: 1px solid #e2e8f0;
}

.border-right {
	border-right: 1px solid #eee;
}

.highlighted-duty {
	background: #eeeeee;
}
</style>
